<script>
import getAgeFromDate from '@utils/age-from-date'
import { mapActions, mapGetters } from 'vuex'

export default {
  props: {
    player: {
      type: Object,
      required: true,
      default: () => {},
    },
    isDetailsOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDetailsOpenLocal: false,
      mins_played: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['loggedIn']),
    label() {
      return this.isDetailsOpenLocal
        ? this.$t('expand_player_profile')
        : this.$t('collapse_player_profile')
    },
  },
  created() {
    this.isDetailsOpenLocal = this.isDetailsOpen

    this.getPlayerStatData({ id: this.player.id }).then((response) => {
      this.mins_played = response.mins_played
    })
  },
  methods: {
    ...mapActions('players', ['getPlayerStatData']),
    toggleDetails() {
      this.$emit('openDetails')
      this.isDetailsOpenLocal = !this.isDetailsOpenLocal
    },
    getPlayerFirstTableData(player) {
      const date = new Date(player.contract_expires)
      const contractUntil =
        date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear()

      const values = {
        age: {
          label: this.$t('playerAge'),
          value: getAgeFromDate(player.birth_date),
        },
        country: {
          label: this.$t('playerCountry'),
          value: player.country,
        },
        club: {
          label: this.$t('playerClub'),
          value: player.team.name,
        },
        position: {
          label: this.$t('playerPosition'),
          value: player.position,
        },
      }

      if (this.loggedIn) {
        values.contract = {
          label: this.$t('playerContract'),
          value: contractUntil,
        }
      }

      return values
    },
    getPlayerSecondTableData(player) {
      const data = {
        height: {
          label: this.$t('height'),
          value: player.height + ' cm',
        },
        weight: {
          label: this.$t('weight'),
          value: player.weight + ' kg',
        },
        foot: {
          label: this.$t('playerFoot'),
          value: this.$t(player.foot_name),
        },
      }

      if (this.mins_played) {
        data.mins_played = {
          label: this.$t('mins_played'),
          value: this.mins_played + ' ' + this.$t('min'),
        }
      }

      return data
    },
  },
}
</script>

<template>
  <v-sheet :class="$style.profileHeader">
    <div :class="$style.headshotBaseInfoWrapper">
      <figure :class="$style.profileHeadshot">
        <img
          :src="player.thumb"
          width="70"
          height="70"
          class="mr-2"
          alt="Player image"
        />
      </figure>
      <div>
        <h2
          >{{ player.name }}
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" :class="$style.index" v-on="on">{{
                player.performance_mean_season
              }}</span>
            </template>
            <span>{{ $t('player_index') }}</span>
          </v-tooltip>
        </h2>
        <table :class="$style.headerTable">
          <tr>
            <td>{{ $t('playerClub') }}:</td>
            <td class="pl-2">{{ player.team.name }}</td>
          </tr>
          <tr>
            <td>{{ $t('playerPosition') }}:</td>
            <td class="pl-2">{{ $t('playerPositions.' + player.position) }}</td>
          </tr>
        </table>
      </div>
    </div>
    <div :class="$style.tableWrapper">
      <v-simple-table dense :class="[$style.dataTable, 'v-data-table--xs']">
        <tbody>
          <tr v-for="item in getPlayerFirstTableData(player)" :key="item.name">
            <td :class="$style.wHalf">{{ item.label }}</td>
            <td :class="$style.wHalf">{{ item.value }}</td>
          </tr>
        </tbody>
      </v-simple-table>

      <v-simple-table dense :class="[$style.dataTable, 'v-data-table--xs']">
        <tbody>
          <tr v-for="item in getPlayerSecondTableData(player)" :key="item.name">
            <td :class="$style.wHalf">{{ item.label }}</td>
            <td :class="$style.wHalf">{{ item.value }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </v-sheet>
</template>

<style lang="scss" module>
@import '@design';

.dataTable {
  border-left: 1px solid $color-text;
  border-radius: 0 !important;

  @media screen and (max-width: $screen-md) {
    width: 100%;
  }
  @media screen and (min-width: $screen-lg) {
    width: 19rem;
  }
  @media screen and (min-width: $screen-xl) {
    width: 22rem;
  }
}

.wHalf {
  @media screen and (max-width: $screen-xl) {
    width: 50% !important;
  }
}

.toggleButton {
  width: 9rem;
  margin-top: auto;
}

.headerTable {
  max-width: 30rem;

  td {
    font-size: 14px;
  }
}

.index {
  margin-left: 1rem;
  font-size: $font-size-h4;
  font-weight: bold;
  color: $color-accent;
}

.profileHeadshot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6rem;
  height: 6rem;
}

.profileHeader {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem;
  border-bottom: 4px solid $color-secondary;

  @media screen and (min-width: $screen-lg) {
    flex-direction: row;
  }
}

.headshotBaseInfoWrapper {
  display: flex;
  flex-direction: row;
  @media screen and (max-width: $screen-sm) {
    flex-wrap: wrap;
  }
}

.tableWrapper {
  display: inline;
  @media screen and (min-width: $screen-lg) {
    display: flex;
    flex-wrap: wrap;
    margin-left: auto;
  }
}
</style>
